import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { Button, ProgressBar, Spinner } from 'react-bootstrap';

export interface ImageCaptureProps {
    onImageSelected: (file: File) => void;
    isUploading: boolean;
    uploadProgress: number;
}

export function ImageCapture({ onImageSelected, isUploading, uploadProgress }: ImageCaptureProps) {
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (!selectedFile) {
            setPreviewUrl(null);
            return;
        }
        const reader = new FileReader();
        reader.onload = () => {
            if (typeof reader.result === 'string') setPreviewUrl(reader.result);
        };
        reader.onerror = () => setPreviewUrl(null);
        reader.readAsDataURL(selectedFile);
        return () => { if (reader.readyState === FileReader.LOADING) reader.abort(); };
    }, [selectedFile]);

    const handleFileChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        setSelectedFile(event.target.files?.[0] ?? null);
        // reset input value so re-selecting same file still fires onChange
        event.target.value = '';
    }, []);

    const handleAnalyze = useCallback(() => {
        if (selectedFile) onImageSelected(selectedFile);
    }, [onImageSelected, selectedFile]);

    return (
        <div className="capture-container">
            {/* Hidden native file input */}
            <input
                ref={inputRef}
                type="file"
                accept="image/png,image/jpeg,image/jpg"
                capture="environment"
                onChange={handleFileChange}
                disabled={isUploading}
                style={{ display: 'none' }}
            />

            {!selectedFile ? (
                /* ── Empty state ── */
                <div className="capture-empty">
                    <i className="bi bi-camera capture-icon" aria-hidden="true" />
                    <p className="capture-hint">Take or upload a sharp photo of the full cassette</p>
                    <Button
                        variant="outline-light"
                        size="lg"
                        className="capture-btn"
                        onClick={() => inputRef.current?.click()}
                    >
                        <i className="bi bi-camera me-2" aria-hidden="true" />
                        Take an Image
                    </Button>
                </div>
            ) : (
                /* ── Preview state ── */
                <div className="capture-preview">
                    <img
                        src={previewUrl ?? ''}
                        alt={selectedFile.name}
                        className={`capture-preview-img${isUploading ? ' capture-preview-img--dim' : ''}`}
                    />

                    {isUploading && (
                        <ProgressBar
                            now={uploadProgress}
                            striped
                            animated
                            className="capture-progress"
                        />
                    )}

                    <div className="capture-actions">
                        <Button
                            variant="outline-secondary"
                            onClick={() => { setSelectedFile(null); inputRef.current?.click(); }}
                            disabled={isUploading}
                        >
                            <i className="bi bi-arrow-repeat me-2" aria-hidden="true" />
                            Retake
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleAnalyze}
                            disabled={isUploading}
                        >
                            {isUploading ? (
                                <>
                                    <Spinner as="span" animation="border" size="sm" className="me-2" />
                                    Analyzing…
                                </>
                            ) : (
                                <>
                                    Analyze Image
                                    <i className="bi bi-arrow-right ms-2" aria-hidden="true" />
                                </>
                            )}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}
